<template>
  <div>
    <v-dialog
      v-model="mainDialogActive"
      persistent
      max-width="1000"
      class="main-dialog"
    >
      <v-card>
        <v-container fluid grid-list-md>
          <v-layout column wrap>
            <!-- Title of dialog -->
            <h2 class="mt-2">{{ $translate("PRODUCT_CATEGORIES") }}</h2>

            <!-- Button to create new product category -->
            <v-flex>
              <v-layout justify-end class="mr-4">
                <v-btn
                  round
                  color="secondary"
                  class="mt-4"
                  @click="addProductCategoryLocally"
                >
                  <v-icon>add</v-icon>
                </v-btn>
              </v-layout>
            </v-flex>

            <v-flex
              xs12
              class="my-4"
              :style="`border: 1px solid black; border-radius: 5px`"
            >
              <v-layout row wrap justify-space-around class="my-4">
                <!-- List of product categories -->
                <v-flex xs5>
                  <v-card>
                    <v-list>
                      <draggable v-model="productCategories">
                        <template v-for="(item, index) in productCategories">
                          <v-list-tile :key="index" avatar @click="">
                            <v-list-tile-avatar color="indigo">
                              <span style="color: white">{{ index + 1 }}</span>
                            </v-list-tile-avatar>
                            <v-list-tile-content
                              @click="selectProductCategory(index)"
                            >
                              <v-list-tile-title>{{
                                item.Name
                              }}</v-list-tile-title>
                            </v-list-tile-content>
                            <v-list-tile-action>
                              <v-btn @click="deleteProductCategory(index)" icon
                                ><v-icon>close</v-icon></v-btn
                              >
                            </v-list-tile-action>
                          </v-list-tile>
                        </template>
                      </draggable>
                    </v-list>
                  </v-card>
                </v-flex>

                <!-- For viewing product category -->
                <v-flex v-if="!editMode" xs6 d-flex text-xs-center>
                  <v-scroll-y-transition mode="out-in">
                    <div
                      v-if="
                        productCategorySelected === undefined ||
                        !productCategorySelected.hasOwnProperty('Id')
                      "
                      class="title grey--text text--lighten-1 font-weight-light"
                      style="align-self: center"
                    >
                      Select a product category
                    </div>
                    <v-card v-else class="pt-4 mx-auto" max-width="500">
                      <v-layout row justify-end class="mr-2">
                        <v-btn round color="primary" @click="enableEditMode">
                          <v-icon dark>edit</v-icon>
                        </v-btn>
                      </v-layout>

                      <v-card-text>
                        <v-avatar size="100">
                          <v-img
                            :src="productCategorySelected.Picture"
                            class="mb-4"
                          ></v-img>
                        </v-avatar>
                        <h3 class="headline mb-2">
                          {{ productCategorySelected.Name }}
                        </h3>
                      </v-card-text>

                      <v-divider></v-divider>

                      <!-- Translations -->
                      <v-subheader
                        >Translations of product category</v-subheader
                      >
                      <v-layout tag="v-card-text" text-xs-left column wrap>
                        <template
                          v-for="(name,
                          languageId) in productCategorySelected.Translations"
                        >
                          <v-flex :key="languageId">
                            <v-layout row wrap justify-center>
                              <v-flex tag="strong" xs3 text-xs-right mr-3 mb-1
                                >{{ getLanguageName(languageId) }}:</v-flex
                              >
                              <v-flex xs3>{{ name }}</v-flex>
                            </v-layout>
                          </v-flex>
                        </template>
                      </v-layout>
                    </v-card>
                  </v-scroll-y-transition>
                </v-flex>

                <!-- For editing product category -->
                <v-flex v-else>
                  <v-card class="pt-4 mx-auto" max-width="500">
                    <v-form
                      ref="categoryForm"
                      v-model="categoryFormValid"
                      lazy-validation
                      class="ma-4"
                    >
                      <v-text-field
                        v-model="categoryForm.Name"
                        :label="$translate('NAME')"
                        required
                        :rules="nameRule"
                        @change="doneBtnActive = true"
                      ></v-text-field>

                      <p>{{ $translate("PRODUCT_CATEGORY_PICTURE") }}</p>
                      <MapIconLoader
                        v-if="editMode"
                        :imageProps="imageProps"
                        v-on:uploadImage="updateImage"
                        :style="`border: 1px solid black; border-radius: 5px`"
                      />

                      <v-divider class="mt-4"></v-divider>
                      <v-subheader
                        >Translations of product category</v-subheader
                      >

                      <template v-for="language in languages">
                        <v-flex :key="language.LanguageId">
                          <h3>
                            {{ language.LocalName }}
                          </h3>
                          <v-text-field
                            v-model="
                              categoryForm.Translations[language.LanguageId]
                            "
                            :label="$translate('NAME')"
                            :rules="nameRule"
                            @change="doneBtnActive = true"
                          ></v-text-field>
                        </v-flex>
                      </template>

                      <v-btn color="secondary" @click="cancelCategoryChanges">{{
                        $translate("CANCEL")
                      }}</v-btn>

                      <v-btn
                        color="primary"
                        :disabled="!doneBtnActive"
                        @click="updateCategoryLocally"
                        >{{ $translate("DONE") }}</v-btn
                      >
                    </v-form>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-container>

        <v-card-actions class="pb-4 m-2">
          <v-spacer></v-spacer>
          <v-btn round color="secondary" @click="cancelAllChanges">
            {{ $translate("CANCEL") }}
          </v-btn>
          <v-btn round color="primary" @click="saveAllChanges">
            {{ $translate("SAVE") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog when request succeeded -->
    <SuccessDialog
      v-if="successDialogActive"
      :successMsg="successMsg"
      @success-dialog-closed="successDialogClosedHandler"
    />

    <!-- Dialog when request failed -->
    <FailureDialog
      v-if="failureDialogActive"
      :failureMsg="failureMsg"
      @failure-dialog-closed="failureDialogClosedHandler"
    />
  </div>
</template>

<script>
import * as dssRequest from "../api/dssRequest";
import { store } from "../store";
import appConfig from "../../config/app_config";
import draggable from "vuedraggable";
import MapIconLoader from "@/components/media/MapIconLoader";
import SuccessDialog from "@/components/AlertDialogs/SuccessDialog";
import FailureDialog from "@/components/AlertDialogs/FailureDialog";

export default {
  name: "ProductCategoriesDialog",
  components: {
    draggable,
    MapIconLoader,
    SuccessDialog,
    FailureDialog,
  },
  props: {
    productCategoriesProp: Array,
    eventId: [Number, String],
  },
  computed: {},
  watch: {},
  data() {
    return {
      mainDialogActive: true,
      productCategories: this.productCategoriesProp,
      productCategorySelected: {},
      categoryForm: {},
      categoryFormValid: false,
      imageProps: {},
      editMode: false,
      languages: [],
      nameRule: [(v) => !!v || "A name for the category is required"],
      doneBtnActive: false,
      successDialogActive: false,
      failureDialogActive: false,
      successMsg: "",
      failureMsg: "",
    };
  },
  mounted() {
    this.$store.getters.getLanguages.forEach((language) => {
      this.languages.push(language);
    });
  },
  methods: {
    selectProductCategory(index) {
      this.productCategorySelected = Object.assign(
        {},
        this.productCategories[index]
      );
    },
    deleteProductCategory(index) {
      if (
        this.productCategorySelected.Id === this.productCategories[index].Id
      ) {
        // Deleting selected product category
        this.productCategorySelected = {};
      }

      this.productCategories.splice(index, 1);
    },
    getLanguageName(languageId) {
      let languagaeName = "";
      let i = this.languages.findIndex(
        (language) => language.LanguageId == languageId
      );

      if (i > -1) {
        languagaeName = this.languages[i].LocalName;
      }

      return languagaeName;
    },
    addProductCategoryLocally() {
      this.categoryForm = {
        Id: "",
        Name: "",
        Picture: "",
        Translations: {},
      };

      this.imageProps = {
        id: "productCategoryImageLoader",
        src: "",
      };

      this.editMode = true;
    },
    enableEditMode() {
      this.categoryForm = {};
      this.categoryForm = Object.assign({}, this.productCategorySelected);
      this.imageProps = {
        id: "productCategoryImageLoader",
        src: this.categoryForm.PictureUrl,
      };
      this.editMode = true;
    },
    updateImage(image) {
      this.categoryForm.Picture = image;
      this.imageProps.src = image;
      this.doneBtnActive = true;
    },
    updateCategoryLocally() {
      let i = this.productCategories.findIndex(
        (category) => category.Id == this.categoryForm.Id
      );

      if (i > -1) {
        // Editing product category
        this.productCategories[i] = Object.assign({}, this.categoryForm);
        this.productCategorySelected = Object.assign({}, this.categoryForm);
      } else {
        // Creating product category
        let categoryTmp = Object.assign({}, this.categoryForm);
        this.productCategories.push(categoryTmp);
        this.productCategorySelected = Object.assign({}, this.categoryForm);
      }

      this.categoryForm = {};
      this.doneBtnActive = false;
      this.editMode = false;
    },
    cancelCategoryChanges() {
      this.categoryForm = {};
      this.doneBtnActive = false;
      this.editMode = false;
    },
    cancelAllChanges() {
      this.mainDialogActive = false;
      this.$emit("product-categories-dialog-closed");
    },
    saveAllChanges() {
      const payload = {
        EventId: this.eventId,
        ProductCategories: [],
      };

      this.productCategories.forEach((category) => {
        payload.ProductCategories.push(category);
      });

      dssRequest.doRequest(
        (response) => {
          console.log(
            "The request [saveProductCategories] to [EventAdministrationMgr] in component [ProductCategoriesDialog] succeeded",
            response
          );

          this.successMsg =
            "Your request to update product categories has been processed successfully.";
          this.successDialogActive = true;
        },
        (error) => {
          console.log(
            "The request [saveProductCategories] to [EventAdministrationMgr] in component [ProductCategoriesDialog] failed",
            error
          );

          this.failureMsg =
            "Your request to update product categories failed. Please try again later. If the problem persists, please contact the administrator.";
          this.failureDialogActive = true;
        },
        "saveProductCategories",
        appConfig.dss.eventAdminMng,
        payload
      );
    },
    successDialogClosedHandler() {
      this.successDialogActive = false;
      this.mainDialogActive = false;
      this.$emit("product-categories-updated", this.productCategories);
    },
    failureDialogClosedHandler() {
      this.failureDialogActive = false;
      this.mainDialogActive = false;
      this.$emit("product-categories-dialog-closed");
    },
  },
};
</script>

<style scoped>
.main-dialog {
  overflow-x: hidden;
  overflow-y: hidden;
  min-height: 200px;
}
</style>